import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Chip,
  Button
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IContactDetails } from '.';
import Loader from 'src/components/Loader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ContactDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [contact, setContact] = useState<IContactDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchContactDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DataService.get(`api/Contact/get/${id}`);
      if (response.ok) {
        const res = await response.json();
        setContact(res.data);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch contact details', type: 'error' }));
        navigate('/contacts');
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading contact details', type: 'error' }));
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  }, [id, dispatch, navigate]);

  useEffect(() => {
    fetchContactDetails();
  }, [fetchContactDetails]);

  if (loading || !contact) {
    return (
      <Box>
        {/* <Helmet>
          <title>Contact Details</title>
        </Helmet>
        <MainPageTitle title="Contact Details" /> */}
        <Loader />
      </Box>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const DetailRow: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Typography variant="body1">
          {value || 'N/A'}
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <div>
      {/* <Helmet>
        <title>Contact Details - {contact.fullName}</title>
      </Helmet> */}

      <Box sx={{ mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/contacts')}
          sx={{ mb: 1 }}
        >
          Contact Details
        </Button>
        {/* <MainPageTitle title="Contact Details" /> */}
      </Box>

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
              {contact.fullName}
            </Typography>
            <Chip
              label={contact.active ? 'Active' : 'Inactive'}
              color={contact.active ? 'success' : 'error'}
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
            Personal Information
          </Typography>
          <DetailRow label="Full Name" value={contact.fullName} />
          <DetailRow label="Email" value={contact.email} />
          <DetailRow label="Mobile Number" value={contact.mobileNumber} />
          <DetailRow label="Date of Birth" value={formatDate(contact.dateOfBirth)} />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'primary.main' }}>
            Identity Documents
          </Typography>
          <DetailRow label="Passport Number" value={contact.passportNumber} />
          <DetailRow label="Passport Expiry Date" value={formatDate(contact.passportExpiryDate)} />
          <DetailRow label="National ID Number" value={contact.nationalIdNumber} />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'primary.main' }}>
            Address Information
          </Typography>
          <DetailRow label="Address in Lebanon" value={contact.addressInLebanon} />
          <DetailRow label="Address in Kinshasa" value={contact.addressInKinshasa} />
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactDetails;
